import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IAppRequest, IAppServiceLanguages, InterfaceLayerMunicipalities } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { SettingMapService } from '@modules/settings/services/setting-map.service';

@Component({
    selector: 'app-table-layer-dialog',
    templateUrl: './table-layer-dialog.component.html',
})
export class TableLayerDialogComponent implements OnInit {
    /**
     * Список мультиязычных полей форм
     */
    readonly #languageFormFields = [
        { name: 'name', required: true },
        { name: 'coordinate_system', required: true },
        { name: 'description', required: false },
    ];
    /** Объект формы */
    form: FormGroup = null;
    /**  Ссылка на объект формы  */
    @ViewChild('formRef', { static: true }) formRef: ElementRef;
    /** Текущий язык формы */
    currentLangForm: IAppServiceLanguages = {};
    /** Инициализация формы */
    formInit: boolean = false;

    /** Список доступных систем координат */
    coordinateSystems: InterfaceLayerMunicipalities[] = [];

    /** Система координат карты */
    mapProjection: string = '';

    constructor(
        private formBuilder: FormBuilder,
        public appService: AppService,
        public settingMapService: SettingMapService,
        public notifier: NotifierService,
        public dialog: MatDialog,
        public spinner: NgxSpinnerService,
        public dialogRef: MatDialogRef<TableLayerDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            title: string;
            tableService: any;
            query: any;
            dynamicFilter: any;
            id: number;
            data: any;
        },
    ) {
        this.currentLangForm = AppHelper.getCurrentLanguage(this.appService);
    }

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        this.form = this.formBuilder.group({});

        this.spinner.show();

        this.settingMapService
            .getSettings(null)
            .toPromise()
            .then((mapSettings: IAppRequest) => {
                this.mapProjection = mapSettings?.data?.projection;

                // Запрос списка систем координат
                this.appService
                    .getRegisters(['coordinate_systems_admin_selection'])
                    .toPromise()
                    .then((response: IAppRequest) => {
                        if (response && response.is_error === false) {
                            this.coordinateSystems = response.data.coordinate_systems_admin_selection;
                        } else {
                            this.notifier.notify('error', response.message);
                        }
                    })
                    .catch(() => {
                        this.notifier.notify(
                            'error',
                            this.appService.language_dictionary.system_no_connection_with_system,
                        );
                    })
                    .finally(() => {
                        this.spinner.hide();
                        this.initForm();
                    });
            });
    }

    /**
     * Установить язык ввода
     * @param lang объект языка
     */
    setCurrentLangForm(lang: IAppServiceLanguages) {
        this.currentLangForm = lang;
    }

    /** Инициализация формы */
    initForm() {
        this.form = this.formBuilder.group({});
        this.#languageFormFields.forEach((field) => {
            const formGroup = this.formBuilder.group({});
            this.form.addControl(field.name, formGroup);
            AppHelper.createLangField(
                this.appService,
                this.formBuilder,
                formGroup,
                field.name,
                this.getFieldDefaultLangValue(field.name),
                field.required,
            );
        });
        setTimeout(() => {
            AppHelper.focusInput(this.formRef.nativeElement.querySelector('.mat-form-field'));
        }, 0);
        this.formInit = true;
    }

    /**
     * Возвращает объект-значение по умолчанию для [некоторых] полей формы.
     */
    getFieldDefaultLangValue(fieldName: string): object {
        if (fieldName === 'coordinate_system') {
            return {
                coordinate_system: {
                    [this.currentLangForm?.lang]: this.mapProjection,
                },
            };
        }

        return {};
    }

    /**
     * Обработка события нажатия на Enter
     * @param {object} event объект события
     */
    keyDownFunction(event): void {
        if (event.code == 'Enter') {
            this.save();
        }
    }

    /**
     * Сохранение данных
     */
    save() {
        if (this.form.valid) {
            this.spinner.show();
            let data = Object.assign({}, this.form.value);
            if (this.data.dynamicFilter) {
                data = Object.assign(data, this.data.dynamicFilter);
            }
            if (this.data.id > 0) {
                data.id = this.data.id;
            }
            if (this.data.query) {
                data.query = this.data.query;
            }
            this.data.tableService
                .saveAsLayer(data)
                .toPromise()
                .then((result: IAppRequest) => {
                    this.spinner.hide();
                    if (result && result.is_error == false) {
                        this.dialogRef.close({
                            save: true,
                            id: result.data,
                        });
                    } else {
                        if (result && result.is_error == true) {
                            this.notifier.notify('error', result.message);
                        }
                        for (let field in result.data) {
                            if (result.data.hasOwnProperty(field) && result.data[field]) {
                                const prop = this.form.get(field);
                                if (prop) {
                                    prop.setErrors({
                                        serverError: result.data[field],
                                    });
                                }
                            }
                        }
                        AppHelper.validateAllFormFields(this.form);
                        setTimeout(() => {
                            const target = this.formRef.nativeElement.querySelector(
                                '.mat-select.ng-invalid, .mat-input-element.ng-invalid, .mat-checkbox-input.ng-invalid',
                            );
                            AppHelper.focusInput(target);
                        }, 0);
                    }
                })
                .catch(() => {
                    this.spinner.hide();
                    this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
                });
        } else {
            AppHelper.validateAllFormFields(this.form);
            AppHelper.focusFirstInvalidField(this.formRef);
        }
    }

    /**
     * закрытие диалога
     */
    close() {
        this.dialogRef.close({ save: false });
    }
}
